import PropTypes from 'prop-types'
import {
  MapPin,
  Filter,
  BarChart3,
  AlertCircle,
  Lightbulb,
  Building2,
} from 'lucide-react'

/**
 * QueryResponse Component
 *
 * Displays different types of query responses with appropriate formatting
 * and action buttons.
 */
export default function QueryResponse({
  response,
  onApplyFilters,
  onSwitchView,
  onClear,
}) {
  if (!response) return null

  const {
    type,
    text,
    data,
    filters,
    suggestedView,
    suggestedAction,
    rankings,
    suggestions,
    insights,
    timestamp,
    facilities,
    resultCount,
    cached,
    // tokensUsed - available but not currently displayed
  } = response

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Cache Indicator Badge */}
      {cached && (
        <div className="px-4 pt-3 pb-0">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium border border-blue-200">
            <span className="text-sm">⚡</span>
            <span>Instant result (cached)</span>
          </div>
        </div>
      )}

      {/* Response Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start gap-3">
          {type === 'answer' && (
            <div className="p-2 bg-blue-50 rounded-lg">
              <Lightbulb className="w-5 h-5 text-blue-600" />
            </div>
          )}
          {type === 'filter' && (
            <div className="p-2 bg-green-50 rounded-lg">
              <Filter className="w-5 h-5 text-green-600" />
            </div>
          )}
          {type === 'comparison' && (
            <div className="p-2 bg-purple-50 rounded-lg">
              <BarChart3 className="w-5 h-5 text-purple-600" />
            </div>
          )}
          {type === 'clarification' && (
            <div className="p-2 bg-yellow-50 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            </div>
          )}
          {type === 'error' && (
            <div className="p-2 bg-red-50 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
          )}

          <div className="flex-1">
            <p className="text-gray-800 leading-relaxed">{text}</p>
          </div>
        </div>
      </div>

      {/* Response Content Based on Type */}
      {type === 'answer' && data && (
        <div className="p-4 space-y-2">
          {data.count !== undefined && (
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">
                <span className="font-semibold text-gray-800">
                  {data.count}
                </span>{' '}
                {data.total !== undefined && `out of ${data.total}`} facilities
              </span>
            </div>
          )}
          {data.percentage !== undefined && (
            <div className="text-sm text-gray-600">
              Coverage:{' '}
              <span className="font-semibold text-gray-800">
                {data.percentage}%
              </span>
            </div>
          )}
          {data.location && (
            <div className="text-sm text-gray-500">
              Location: {data.location}
            </div>
          )}
        </div>
      )}

      {type === 'filter' && (
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              {resultCount || facilities?.length || 0} results found
            </span>
          </div>

          {/* Display matching facilities */}
          {facilities && facilities.length > 0 && (
            <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="text-xs font-semibold text-gray-600 uppercase mb-2">
                Matching Facilities
              </div>
              {facilities.slice(0, 5).map((facility, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-white rounded border border-gray-200 hover:border-green-400 transition-colors"
                >
                  <div className="font-medium text-gray-900 text-sm">
                    {facility.institution_name ||
                      facility.facility_name ||
                      facility.name}
                  </div>
                  <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {facility.subcounty || facility.district}
                    </div>
                    <div className="flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {facility.ownership || facility.facility_level}
                    </div>
                    {facility.total_learners && (
                      <div>
                        👥 {facility.total_learners} learners •{' '}
                        {facility.total_teachers} teachers
                      </div>
                    )}
                    {facility.hasImmunization && (
                      <div>
                        💉 Immunization:{' '}
                        {facility.hasImmunization === 'Yes' ? '✓' : '✗'}
                      </div>
                    )}
                    {facility.electricity_available && (
                      <div>
                        ⚡ Electricity:{' '}
                        {facility.electricity_available === 'Yes' ? '✓' : '✗'}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {facilities.length > 5 && (
                <div className="text-xs text-gray-500 text-center pt-2 border-t border-gray-200">
                  + {facilities.length - 5} more facilities
                </div>
              )}
            </div>
          )}

          {onApplyFilters && filters && (
            <button
              onClick={() => onApplyFilters(filters)}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
            >
              Apply Filters & View Results
            </button>
          )}

          {suggestedView && onSwitchView && (
            <button
              onClick={() => onSwitchView(suggestedView)}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
            >
              Switch to{' '}
              {suggestedView.charAt(0).toUpperCase() + suggestedView.slice(1)}{' '}
              View
            </button>
          )}
        </div>
      )}

      {type === 'comparison' && rankings && rankings.length > 0 && (
        <div className="p-4 space-y-3">
          {/* AI Insights for comparison */}
          {insights && (
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-100 mb-3">
              <div className="flex items-start gap-2">
                <Lightbulb className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-purple-900">{insights}</p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {rankings.slice(0, 5).map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 rounded"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-500 w-6">
                    #{index + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-800">
                    {item.location}
                  </span>
                </div>
                <span className="text-sm font-semibold text-purple-600">
                  {item.value} {item.metric}
                </span>
              </div>
            ))}
          </div>

          {rankings.length > 5 && (
            <div className="text-sm text-gray-500 text-center">
              + {rankings.length - 5} more locations
            </div>
          )}

          {suggestedView && onSwitchView && (
            <button
              onClick={() => onSwitchView(suggestedView)}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
            >
              View Full Comparison
            </button>
          )}
        </div>
      )}

      {/* Suggested Action - Show for all types */}
      {suggestedAction && (
        <div className="px-4 pb-3 pt-2">
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">💡 Suggested Action:</span>{' '}
              {suggestedAction}
            </p>
          </div>
        </div>
      )}

      {/* Suggestions for unclear queries */}
      {suggestions && suggestions.length > 0 && (
        <div className="p-4 border-t border-gray-100 space-y-2">
          <p className="text-sm font-medium text-gray-700">Try asking:</p>
          <div className="space-y-1">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                className="text-sm text-blue-600 hover:text-blue-700 cursor-pointer block text-left w-full"
                onClick={() => {
                  // This will be handled by parent component
                  // eslint-disable-next-line no-undef
                  const event = new CustomEvent('suggestion-click', {
                    detail: { suggestion },
                  })
                  window.dispatchEvent(event)
                }}
              >
                • {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Timestamp */}
      {timestamp && (
        <div className="px-4 pb-2 text-xs text-gray-500 text-right border-t border-gray-100 pt-2">
          {new Date(timestamp).toLocaleString()}
        </div>
      )}

      {/* Clear button */}
      {onClear && (
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={onClear}
            className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
          >
            Clear Results
          </button>
        </div>
      )}
    </div>
  )
}

QueryResponse.propTypes = {
  response: PropTypes.shape({
    type: PropTypes.oneOf([
      'answer',
      'filter',
      'comparison',
      'error',
      'clarification',
    ]).isRequired,
    text: PropTypes.string.isRequired,
    timestamp: PropTypes.string,
    suggestedView: PropTypes.string,
    suggestedAction: PropTypes.string,
    insights: PropTypes.string,

    // API v2 fields
    cached: PropTypes.bool,
    cacheKey: PropTypes.string,
    tokensUsed: PropTypes.number,
    visualizations: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        type: PropTypes.oneOf(['echarts', 'leaflet']),
        config: PropTypes.object,
      })
    ),

    // answer type
    data: PropTypes.object,

    // filter type
    filters: PropTypes.object,
    resultCount: PropTypes.number,
    facilities: PropTypes.arrayOf(PropTypes.object),

    // comparison type
    rankings: PropTypes.arrayOf(
      PropTypes.shape({
        location: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
          .isRequired,
        metric: PropTypes.string.isRequired,
      })
    ),

    // clarification/answer types
    suggestions: PropTypes.arrayOf(PropTypes.string),
  }),
  onApplyFilters: PropTypes.func,
  onSwitchView: PropTypes.func,
  onClear: PropTypes.func,
}
